import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { CHAPTERS, CUES, Clock, Piece, SCENES, TOTAL } from '../components/PresentationPiece';
import { MadeBy } from '../components/MadeBy';
import './Apresentacao.css';

const W = 1920;
const H = 1080;
// Movimento reduzido: um slide por cena, no quadro em que tudo já apareceu
// Trilha com a mesma duração da peça; só é baixada quando a pessoa ativa o som
const MUSIC = '/apresentacao-musica.mp3';
const SLIDES = SCENES.map(([name, dur]) => ({ name, T: CUES[name] + dur - (name === 'Fechamento' ? 1.2 : 0.75) }));

/** Apresentação pública (/apresentacao): vídeo de 1min40 em loop, com play/pause, som e capítulos. */
function Apresentacao() {
  const navigate = useNavigate();
  const reduce = useMemo(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches, []);
  const [T, setT] = useState(reduce ? SLIDES[0].T : 0);
  const [playing, setPlaying] = useState(!reduce);
  const [slide, setSlide] = useState(0);
  const [scale, setScale] = useState(0.5);
  const [sound, setSound] = useState(false);
  const frameRef = useRef(null);
  const tRef = useRef(T);
  const audioRef = useRef(null);

  useEffect(() => {
    document.title = 'StudySpace · Apresentação';
    return () => { document.title = 'StudySpace — Estudo sem limite'; };
  }, []);

  // A peça de 1920×1080 é escalada para caber na largura e na altura disponíveis
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const fit = () => setScale(el.clientWidth / W);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Relógio: T = tempo da peça, em loop. Pausa quando a aba some (rAF para) e não pula ao voltar.
  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.25, (now - last) / 1000);
      last = now;
      tRef.current = (tRef.current + dt) % TOTAL;
      setT(tRef.current);
      // A música segue o relógio da peça: corrige o desvio (e a volta do loop) quando passa de 0,3 s
      const a = audioRef.current;
      if (a && !a.paused && Math.abs(a.currentTime - tRef.current) > 0.3) a.currentTime = tRef.current;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const seek = useCallback((t) => {
    tRef.current = Math.max(0, Math.min(TOTAL - 0.01, t));
    setT(tRef.current);
    if (audioRef.current) audioRef.current.currentTime = tRef.current;
  }, []);

  // Som: toca só com o vídeo rodando, a aba visível e o botão ligado
  useEffect(() => {
    if (!sound && !audioRef.current) return undefined;
    if (!audioRef.current) {
      audioRef.current = new Audio(MUSIC);
      audioRef.current.preload = 'auto';
    }
    const a = audioRef.current;
    const sync = () => {
      if (sound && playing && !document.hidden) {
        a.currentTime = tRef.current;
        a.play().catch(() => setSound(false));
      } else {
        a.pause();
      }
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [sound, playing]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const goSlide = useCallback((i) => {
    const n = (i + SLIDES.length) % SLIDES.length;
    setSlide(n);
    seek(SLIDES[n].T);
  }, [seek]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.('input, button, a')) return;
      if (e.key === ' ') { e.preventDefault(); if (!reduce) setPlaying(p => !p); }
      if (reduce && e.key === 'ArrowRight') goSlide(slide + 1);
      if (reduce && e.key === 'ArrowLeft') goSlide(slide - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reduce, slide, goSlide]);

  const clock = useMemo(() => ({ T, CUES, authoredTotal: TOTAL }), [T]);
  const current = [...SCENES].reverse().find(([name]) => T >= CUES[name])?.[0];

  return (
    <div className="apr">
      <div className="apr__frame" ref={frameRef} aria-label="Apresentação do StudySpace" role="img">
        <div className="apr__stage" style={{ width: W, height: H, transform: `scale(${scale})` }}>
          <Clock.Provider value={clock}>
            <Piece rail />
          </Clock.Provider>
        </div>
      </div>

      <div className="apr__controls">
        {reduce ? (
          <>
            <button type="button" className="apr__icon" onClick={() => goSlide(slide - 1)} aria-label="Slide anterior"><ChevronLeft size={20} /></button>
            <span className="apr__time">{slide + 1} de {SLIDES.length}</span>
            <button type="button" className="apr__icon" onClick={() => goSlide(slide + 1)} aria-label="Próximo slide"><ChevronRight size={20} /></button>
          </>
        ) : (
          <>
            <button type="button" className="apr__icon" onClick={() => setPlaying(p => !p)} aria-label={playing ? 'Pausar' : 'Reproduzir'}>
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button type="button" className="apr__icon" onClick={() => seek(0)} aria-label="Recomeçar"><RotateCcw size={17} /></button>
            <button
              type="button"
              className={`apr__sound ${sound ? 'is-on' : ''}`}
              onClick={() => setSound(v => !v)}
              aria-pressed={sound}
              title="Música: BreakzStudios (Pixabay)"
            >
              {sound ? <Volume2 size={17} aria-hidden="true" /> : <VolumeX size={17} aria-hidden="true" />}
              {sound ? 'Som ligado' : 'Ativar som'}
            </button>
            <input
              className="apr__seek"
              type="range"
              min={0}
              max={TOTAL}
              step={0.1}
              value={T}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Posição da apresentação"
            />
            <span className="apr__time">{fmt(T)} / {fmt(TOTAL)}</span>
          </>
        )}
        <span className="apr__chapters">
          {CHAPTERS.map(([key, label, scenes], i) => (
            <button key={key} type="button" className={scenes.includes(current) ? 'is-on' : ''} onClick={() => (reduce ? goSlide(SCENES.findIndex(s => s[0] === key)) : seek(CUES[key] + 0.05))}>
              <code>0{i + 1}</code>{label}
            </button>
          ))}
        </span>
      </div>

      <div className="apr__cta">
        <button type="button" className="apr__primary" onClick={() => navigate('/solicitar-acesso')}>Solicitar acesso <ArrowRight size={18} aria-hidden="true" /></button>
        <button type="button" className="apr__ghost" onClick={() => navigate('/')}><ArrowLeft size={18} aria-hidden="true" />Voltar para o site</button>
      </div>
      <MadeBy onDark />
    </div>
  );
}

function fmt(t) {
  const s = Math.floor(t);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default Apresentacao;
